import React, { useState } from "react";
import { ArrowLeft, ArrowRight, Save } from "lucide-react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import Toast from "../Toast/Toast";

const ManualImport = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [toast, setToast] = useState(null);
  const { user } = useAuth();

  const [tradeData, setTradeData] = useState({
    // Basic Trade Info
    symbol: "",
    direction: "LONG",
    quantity: "",
    entryPrice: "",
    entryDate: "",
    entryTime: "",
    instrumentType: "EQUITY", // New field

    // Exit Info
    exitPrice: "",
    exitDate: "",
    exitTime: "",

    // Analysis Info
    setup: "",
    strategy: "",
    risk: "",
    reward: "",
    notes: "",
    tags: [],
  });

  const steps = [
    {
      title: "Entry Details",
      subtitle: "Enter your trade entry information",
    },
    {
      title: "Exit Details",
      subtitle: "Enter your trade exit information",
    },
    {
      title: "Analysis",
      subtitle: "Add trade analysis and metrics",
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTradeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const calculatePnL = () => {
    const entry = parseFloat(tradeData.entryPrice);
    const exit = parseFloat(tradeData.exitPrice);
    const quantity = parseFloat(tradeData.quantity);
    const multiplier = tradeData.instrumentType === "OPTIONS" ? 100 : 1;

    if (!entry || !exit || !quantity) return 0;

    return tradeData.direction === "LONG"
      ? (exit - entry) * quantity * multiplier
      : (entry - exit) * quantity * multiplier;
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return (
          tradeData.symbol &&
          tradeData.entryPrice &&
          tradeData.quantity &&
          tradeData.entryDate &&
          tradeData.entryTime &&
          tradeData.instrumentType
        );
      case 2:
        return tradeData.exitPrice && tradeData.exitDate && tradeData.exitTime;
      case 3:
        return true; // Analysis fields are optional
      default:
        return false;
    }
  };

  const formatDateForFirebase = (date, time) => {
    return new Date(`${date}T${time}`).toISOString();
  };

  const prepareTradeForFirebase = () => {
    const pnl = calculatePnL();

    return {
      // Standard trade fields
      symbol: tradeData.symbol.toUpperCase(),
      direction: tradeData.direction,
      quantity: parseFloat(tradeData.quantity),
      entryPrice: parseFloat(tradeData.entryPrice),
      exitPrice: parseFloat(tradeData.exitPrice),
      date: formatDateForFirebase(tradeData.entryDate, tradeData.entryTime),
      exitDate: formatDateForFirebase(tradeData.exitDate, tradeData.exitTime),
      instrumentType: tradeData.instrumentType,
      pnl: pnl,

      // Analysis fields
      setup: tradeData.setup || "",
      strategy: tradeData.strategy || "",
      risk: parseFloat(tradeData.risk) || 0,
      reward: parseFloat(tradeData.reward) || 0,
      notes: tradeData.notes || "",
      tags: tradeData.tags || [],

      // Meta fields
      status: pnl >= 0 ? "WIN" : "LOSS",
      userId: user.uid,
      broker: "manual",
      timestamp: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setToast({
        type: "error",
        message: "Authentication Error",
        subText: "Please sign in to submit trades",
      });
      return;
    }

    try {
      const tradeForFirebase = prepareTradeForFirebase();
      await addDoc(collection(db, "trades"), tradeForFirebase);

      // Trigger trade update event
      window.dispatchEvent(new Event("tradesUpdated"));

      setToast({
        type: "success",
        message: "Trade Saved Successfully",
        subText: `${tradeData.symbol} trade has been added to your log`,
      });

      // Reset form
      setTradeData({
        symbol: "",
        direction: "LONG",
        quantity: "",
        entryPrice: "",
        entryDate: "",
        entryTime: "",
        instrumentType: "EQUITY",
        exitPrice: "",
        exitDate: "",
        exitTime: "",
        setup: "",
        strategy: "",
        risk: "",
        reward: "",
        notes: "",
        tags: [],
      });
      setCurrentStep(1);
    } catch (error) {
      console.error("Error saving trade:", error);
      setToast({
        type: "error",
        message: "Error Saving Trade",
        subText: "Please try again",
      });
    }
  };

  const renderStep1 = () => (
    <div className="step-content">
      <div className="form-group">
        <label htmlFor="symbol">Symbol</label>
        <input
          type="text"
          id="symbol"
          name="symbol"
          value={tradeData.symbol}
          onChange={handleChange}
          placeholder="e.g., AAPL"
          className="uppercase"
          required
        />
      </div>

      <div className="form-group">
        <label>Instrument Type</label>
        <div className="radio-group">
          <label className="radio-label">
            <input
              type="radio"
              name="instrumentType"
              value="EQUITY"
              checked={tradeData.instrumentType === "EQUITY"}
              onChange={handleChange}
            />
            Equity
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="instrumentType"
              value="OPTIONS"
              checked={tradeData.instrumentType === "OPTIONS"}
              onChange={handleChange}
            />
            Options
          </label>
        </div>
      </div>

      <div className="form-group">
        <label>Direction</label>
        <div className="radio-group">
          <label className="radio-label">
            <input
              type="radio"
              name="direction"
              value="LONG"
              checked={tradeData.direction === "LONG"}
              onChange={handleChange}
            />
            Long
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="direction"
              value="SHORT"
              checked={tradeData.direction === "SHORT"}
              onChange={handleChange}
            />
            Short
          </label>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="entryDate">Entry Date</label>
          <input
            type="date"
            id="entryDate"
            name="entryDate"
            value={tradeData.entryDate}
            onChange={handleChange}
            max={new Date().toISOString().split("T")[0]}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="entryTime">Entry Time</label>
          <input
            type="time"
            id="entryTime"
            name="entryTime"
            value={tradeData.entryTime}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="entryPrice">Entry Price</label>
          <input
            type="number"
            id="entryPrice"
            name="entryPrice"
            value={tradeData.entryPrice}
            onChange={handleChange}
            placeholder="0.00"
            step="0.01"
            min="0"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="quantity">
            {tradeData.instrumentType === "OPTIONS" ? "Contracts" : "Shares"}
          </label>
          <input
            type="number"
            id="quantity"
            name="quantity"
            value={tradeData.quantity}
            onChange={handleChange}
            placeholder={`Number of ${
              tradeData.instrumentType === "OPTIONS" ? "contracts" : "shares"
            }`}
            min="1"
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="step-content">
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="exitDate">Exit Date</label>
          <input
            type="date"
            id="exitDate"
            name="exitDate"
            value={tradeData.exitDate}
            onChange={handleChange}
            min={tradeData.entryDate}
            max={new Date().toISOString().split("T")[0]}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="exitTime">Exit Time</label>
          <input
            type="time"
            id="exitTime"
            name="exitTime"
            value={tradeData.exitTime}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="exitPrice">Exit Price</label>
        <input
          type="number"
          id="exitPrice"
          name="exitPrice"
          value={tradeData.exitPrice}
          onChange={handleChange}
          placeholder="0.00"
          step="0.01"
          min="0"
          required
        />
      </div>

      {tradeData.exitPrice && (
        <div className="form-group">
          <label>Calculated P&L</label>
          <div
            className={`pnl-preview ${calculatePnL() >= 0 ? "profit" : "loss"}`}
          >
            ${calculatePnL().toFixed(2)}
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="step-content">
      <div className="form-group">
        <label htmlFor="setup">Trade Setup</label>
        <select
          id="setup"
          name="setup"
          value={tradeData.setup}
          onChange={handleChange}
        >
          <option value="">Select a setup...</option>
          <option value="Breakout">Breakout</option>
          <option value="Pullback">Pullback</option>
          <option value="Momentum">Momentum</option>
          <option value="Reversal">Reversal</option>
          <option value="Gap Fill">Gap Fill</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="risk">Risk Amount ($)</label>
          <input
            type="number"
            id="risk"
            name="risk"
            value={tradeData.risk}
            onChange={handleChange}
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        </div>
        <div className="form-group">
          <label htmlFor="reward">Reward Amount ($)</label>
          <input
            type="number"
            id="reward"
            name="reward"
            value={tradeData.reward}
            onChange={handleChange}
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="strategy">Strategy</label>
        <input
          type="text"
          id="strategy"
          name="strategy"
          value={tradeData.strategy}
          onChange={handleChange}
          placeholder="Enter your trading strategy"
        />
      </div>

      <div className="form-group">
        <label htmlFor="notes">Trade Notes</label>
        <textarea
          id="notes"
          name="notes"
          value={tradeData.notes}
          onChange={handleChange}
          placeholder="Add any notes about your trade..."
          rows="4"
        />
      </div>
    </div>
  );

  return (
    <div className="manual-trade-entry">
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          subText={toast.subText}
          onClose={() => setToast(null)}
        />
      )}

      <div className="steps-indicator">
        {steps.map((step, index) => (
          <div
            key={index}
            className={`step ${currentStep >= index + 1 ? "active" : ""}`}
          >
            <div className="step-number">{index + 1}</div>
            <div className="step-text">
              <div className="step-title">{step.title}</div>
              <div className="step-subtitle">{step.subtitle}</div>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="trade-form">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}

        <div className="form-navigation">
          {currentStep > 1 && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCurrentStep((prev) => prev - 1)}
            >
              <ArrowLeft size={20} />
              Back
            </button>
          )}

          {currentStep < 3 ? (
            <button
              type="button"
              onClick={() => setCurrentStep((prev) => prev + 1)}
              disabled={!validateStep(currentStep)}
            >
              Next
              <ArrowRight size={20} />
            </button>
          ) : (
            <button type="submit" disabled={!validateStep(currentStep)}>
              Save Trade
              <Save size={20} />
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ManualImport;
